// WP-0.2 (FX-5): Money — integer minor units with explicit currency. No float math, ever.
// Custom module per DEV_PLAN §2 ("do not use a heavy money lib").
import { type CurrencyCode, currencyInfo, MoneyError } from './currency';

export interface Money {
  readonly currency: CurrencyCode;
  readonly minorUnits: number;
}

/** Construct Money from integer minor units (paisa/cents). */
export function fromMinor(currency: CurrencyCode, minorUnits: number): Money {
  currencyInfo(currency); // throws on unknown currency
  if (!Number.isSafeInteger(minorUnits)) {
    throw new MoneyError(
      `Minor units must be a safe integer (FX-5); got ${String(minorUnits)} ${currency}`,
    );
  }
  // Normalize -0 -> 0: Object.is-based equality (replay-hash determinism, WP-0.3)
  // must never distinguish two zeros produced by different code paths.
  return { currency, minorUnits: minorUnits === 0 ? 0 : minorUnits };
}

/**
 * Parse an exact decimal string ("1234.56") into Money. Strings only — accepting a
 * number here would invite float-contaminated inputs (FX-5).
 */
export function fromMajor(currency: CurrencyCode, major: string): Money {
  const info = currencyInfo(currency);
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(major.trim());
  if (!match) {
    throw new MoneyError(`Not a decimal amount: "${major}"`);
  }
  const [, sign, intPart, fracPart = ''] = match;
  if (fracPart.length > info.decimalDigits) {
    throw new MoneyError(
      `${currency} supports ${String(info.decimalDigits)} decimal places; got "${major}"`,
    );
  }
  const scaled =
    BigInt(intPart ?? '0') * BigInt(info.minorPerMajor) +
    BigInt(fracPart.padEnd(info.decimalDigits, '0') || '0');
  const minor = (sign === '-' ? -1n : 1n) * scaled;
  return fromMinorBigInt(currency, minor);
}

/** Internal: BigInt -> Money with safe-integer range check. */
export function fromMinorBigInt(currency: CurrencyCode, minor: bigint): Money {
  if (minor > BigInt(Number.MAX_SAFE_INTEGER) || minor < BigInt(Number.MIN_SAFE_INTEGER)) {
    throw new MoneyError(`Amount out of safe range: ${minor.toString()} ${currency}`);
  }
  return fromMinor(currency, Number(minor));
}

/** Exact decimal-string rendering, always showing the currency's full decimal digits. */
export function toDecimalString(m: Money): string {
  const info = currencyInfo(m.currency);
  const abs = m.minorUnits < 0 ? BigInt(-m.minorUnits) : BigInt(m.minorUnits);
  const per = BigInt(info.minorPerMajor);
  const intPart = (abs / per).toString();
  const fracPart = (abs % per).toString().padStart(info.decimalDigits, '0');
  return `${m.minorUnits < 0 ? '-' : ''}${intPart}.${fracPart}`;
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new MoneyError(
      `Cross-currency operation: ${a.currency} vs ${b.currency} (convert explicitly per M7)`,
    );
  }
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return fromMinorBigInt(a.currency, BigInt(a.minorUnits) + BigInt(b.minorUnits));
}

export function sub(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return fromMinorBigInt(a.currency, BigInt(a.minorUnits) - BigInt(b.minorUnits));
}

export function negate(m: Money): Money {
  return fromMinor(m.currency, -m.minorUnits);
}

export function cmp(a: Money, b: Money): -1 | 0 | 1 {
  assertSameCurrency(a, b);
  if (a.minorUnits < b.minorUnits) return -1;
  if (a.minorUnits > b.minorUnits) return 1;
  return 0;
}

export function isZero(m: Money): boolean {
  return m.minorUnits === 0;
}

/** Exact sum; the explicit currency makes the empty list unambiguous. */
export function sum(currency: CurrencyCode, items: readonly Money[]): Money {
  let total = 0n;
  for (const item of items) {
    if (item.currency !== currency) {
      throw new MoneyError(`sum(${currency}) got a ${item.currency} amount`);
    }
    total += BigInt(item.minorUnits);
  }
  return fromMinorBigInt(currency, total);
}
